import { useRef, useState } from 'react'
import { MessageSquare, Trash2 } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import {
  deviationClass,
  formatDeviation,
  formatPriceInput,
  lineDeviation,
  lineValueCents,
  packageCells,
  parsePriceInput,
  perPackageCents,
  perPieceCents,
} from '../lib/delivery-lines'
import type { CheckLine } from '../lib/delivery-lines'
import type { DeliveryLastPrice } from '@/api/endpoints/inventory-deliveries'
import type { SelectedProduct } from '@/features/products/components/product-sku-combobox'
import type { StockArticleInfo } from '@/features/inventory/hooks/use-stock-info'
import type { NoStockReason } from '@/shared/lib/stock-kind'
import { formatCurrency } from '@/shared/utils/format-currency'
import { ProductSkuCombobox } from '@/features/products/components/product-sku-combobox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DEFAULT_STOCK_UNIT } from '@/features/inventory/hooks/use-stock-info'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'
import { cn } from '@/lib/utils'

/**
 * One position of a delivery (2026-10-03 layout, CHG-2026-09-webbill-delivery-import task 9).
 *
 * Every value has its own labeled field — never a sub-text line under the product name. The same
 * markup is a table row once the list is wide enough (container query on '@container/lines',
 * since the backoffice sidebar eats into the viewport) and a card with a labeled 3-column field
 * grid below that, which is what a staff member checking a delivery at the back door holds.
 */

export interface FormLine {
  /** Stable across re-orders and product changes, so React keys survive editing. */
  key: string
  product: SelectedProduct | null
  /** Raw input; validated on submit. */
  expectedQty: string
  note: string
  /**
   * Optional net purchase price per piece as typed ("2,70"); '' is no price. Only a
   * hand-announced delivery shows and sends it (task 9.5).
   */
  price: string
  /** Set on a line created by Enter so its product picker opens by itself; cleared after use. */
  autoFocus?: boolean
  /** The saved line's stock facts (unit, counting mode) as the server sent them. */
  stock?: StockArticleInfo
}

/** The wrapper every position list sits in, so the rows can switch layout by its width. */
export const LINES_CONTAINER = '@container/lines'

/**
 * The table templates, spelled out in full so Tailwind sees every class. Columns: Produkt,
 * Gebinde, Anzahl, Inhalt, Erwartet, Erhalten, Abw., [EK / Stk, Wert], [actions].
 */
const CHECK_COLUMNS = {
  pricedActions:
    '@4xl/lines:grid-cols-[minmax(8rem,1fr)_5.5rem_3.5rem_4rem_4.5rem_5rem_3.5rem_7rem_5.5rem_4.5rem]',
  priced:
    '@4xl/lines:grid-cols-[minmax(8rem,1fr)_5.5rem_3.5rem_4rem_4.5rem_4.5rem_3.5rem_5.5rem_6rem]',
  actions:
    '@4xl/lines:grid-cols-[minmax(8rem,1fr)_5.5rem_3.5rem_4rem_4.5rem_5rem_3.5rem_4.5rem]',
  plain:
    '@4xl/lines:grid-cols-[minmax(8rem,1fr)_5.5rem_3.5rem_4rem_4.5rem_4.5rem_3.5rem]',
} as const

function checkColumns(showPrices: boolean, withActions: boolean): string {
  if (showPrices)
    return withActions ? CHECK_COLUMNS.pricedActions : CHECK_COLUMNS.priced
  return withActions ? CHECK_COLUMNS.actions : CHECK_COLUMNS.plain
}

/** Columns: Produkt, Menge (Stk), [EK netto / Stk, Letzter EK], Notiz, remove. */
const FORM_COLUMNS = {
  priced:
    '@4xl/lines:grid-cols-[minmax(12rem,1fr)_7rem_7.5rem_7.5rem_minmax(8rem,12rem)_2.5rem]',
  plain:
    '@4xl/lines:grid-cols-[minmax(12rem,1fr)_7rem_minmax(8rem,16rem)_2.5rem]',
} as const

const ROW_BASE =
  'grid grid-cols-3 items-start gap-x-3 gap-y-3 border-t px-3 py-3 @4xl/lines:items-center'
const HEADER_BASE =
  'hidden gap-x-3 bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground @4xl/lines:grid'

/** "12.09.2026" — the date a last price was booked. */
function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('de-AT')
}

/** "12.09." — the same date where a phone card has little room. */
function formatShortDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' })
}

/**
 * The unit is shown where it says something (CHG-2026-09-stock-usage-and-price-twins, D9): on an
 * article counted in partial amounts, or one whose unit is not the default "Stk".
 */
function shownUnit(
  fractional: boolean | undefined,
  unit: string | undefined,
): string | null {
  if (!unit) return null
  return fractional || unit !== DEFAULT_STOCK_UNIT ? unit : null
}

/** "nicht geführt · kein Bestand", "Gericht – kein eigener Bestand" (D11). */
function NoStockBadge({ reason }: { reason: NoStockReason }) {
  const { t: tInventory } = useTranslation('inventory')
  return (
    <Badge
      variant="outline"
      className="border-warning/50 bg-warning/10 text-warning-foreground"
    >
      {tInventory(`noStock.${reason}`)}
    </Badge>
  )
}

/**
 * One labeled value. The label is visible on the phone card only; in the table the header row
 * carries it. Numbers are right-aligned in the table so the columns line up.
 */
function Field({
  label,
  numeric = true,
  className,
  children,
}: {
  label: string
  numeric?: boolean
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-0.5', className)}>
      <span className="text-xs text-muted-foreground @4xl/lines:hidden">
        {label}
      </span>
      <div
        className={cn(
          'min-w-0',
          numeric && 'tabular-nums @4xl/lines:text-right',
        )}
      >
        {children}
      </div>
    </div>
  )
}

/** The net price input "EK netto / Stk": de-AT decimal comma, optional. */
function PriceInput({
  value,
  label,
  onChange,
}: {
  value: string
  label: string
  onChange: (value: string) => void
}) {
  return (
    <Input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={value}
      aria-label={label}
      aria-invalid={!parsePriceInput(value).ok || undefined}
      placeholder="0,00"
      className="text-right tabular-nums"
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

/** The copy affordance of a last price: arrow towards the input it fills. */
function CopyLastPriceButton({
  onCopy,
  className,
}: {
  onCopy: () => void
  className?: string
}) {
  const { t } = useTranslation('inventory-deliveries')
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn('h-7 px-2 text-xs', className)}
      aria-label={t('price.copyLast')}
      title={t('price.copyLast')}
      onClick={onCopy}
    >
      {t('price.copyShort')}
    </Button>
  )
}

// --- Check and detail view ---

export function DeliveryCheckLineRow({
  line,
  readOnly = false,
  /** A cancelled document never received anything; its received column reads as a dash. */
  cancelled = false,
  /** Whether the EK / Stk and Wert columns exist (a priced or a price-editable delivery). */
  showPrices = false,
  /** The last booked price of this article at the store, for an editable price. */
  lastPrice,
  onChange,
  onRemove,
}: {
  line: CheckLine
  readOnly?: boolean
  cancelled?: boolean
  showPrices?: boolean
  lastPrice?: DeliveryLastPrice
  onChange?: (next: CheckLine) => void
  onRemove?: () => void
}) {
  const { t } = useTranslation('inventory-deliveries')
  const [noteOpen, setNoteOpen] = useState(false)
  const diff = cancelled ? null : lineDeviation(line)
  const unannounced = line.expectedQty === 0
  const pieces = line.unit ?? t('detail.pieces')
  const cells = packageCells(line)
  const baseUnit = shownUnit(line.fractional, line.unit)
  const value = showPrices ? lineValueCents(line) : null
  const perPiece = showPrices ? perPieceCents(line) : null
  const perPackage = showPrices ? perPackageCents(line) : null
  const priceEditable = !readOnly && line.price !== undefined
  const withActions = !readOnly
  const showNote = readOnly ? line.note !== '' : noteOpen || line.note !== ''

  return (
    <div className={cn(ROW_BASE, checkColumns(showPrices, withActions))}>
      <div
        className={cn(
          'min-w-0',
          withActions ? 'col-span-2' : 'col-span-3',
          '@4xl/lines:col-span-1',
        )}
      >
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium">
          <span>{line.name ?? line.sku}</span>
          {unannounced && (
            <Badge variant="outline">{t('detail.notAnnounced')}</Badge>
          )}
          {line.noStock && <NoStockBadge reason={line.noStock} />}
        </p>
        <p className="font-mono text-xs text-muted-foreground">{line.sku}</p>
      </div>

      {withActions && (
        // On the card the actions sit top-right beside the name; in the table, last.
        <div className="col-start-3 row-start-1 flex justify-end gap-1 @4xl/lines:order-last @4xl/lines:col-start-auto @4xl/lines:row-start-auto">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`${t('detail.noteToggle')} ${line.sku}`}
            title={t('detail.noteToggle')}
            aria-pressed={showNote}
            onClick={() => setNoteOpen((open) => !open)}
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`${t('detail.removeLine')} ${line.sku}`}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}

      <Field label={t('detail.column.package')} numeric={false}>
        {cells.kind === 'package' ? (
          <span className="break-words">{cells.name}</span>
        ) : cells.kind === 'unknown' ? (
          <Badge
            variant="outline"
            className="border-warning/50 bg-warning/10 text-warning-foreground"
            title={t('detail.unitWarning')}
          >
            {t('detail.unitUnknown')}
          </Badge>
        ) : (
          <span>{baseUnit ?? t('detail.unitPiece')}</span>
        )}
      </Field>
      <Field label={t('detail.column.unitCount')}>
        {formatStockDisplay(cells.count)}
      </Field>
      <Field label={t('detail.column.content')}>
        {cells.kind === 'package'
          ? `${cells.content} ${pieces}`
          : cells.kind === 'unknown'
            ? '?'
            : '–'}
      </Field>
      <Field label={t('detail.column.expected')}>
        {formatStockDisplay(line.expectedQty)}
      </Field>
      <Field label={t('detail.column.received')}>
        {readOnly ? (
          cancelled ? (
            '—'
          ) : line.received === '' ? (
            '–'
          ) : (
            line.received
          )
        ) : line.fractional ? (
          // "Teilmengen zählen": a text field keeps the de-AT comma a number field drops.
          <Input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={line.received}
            aria-label={`${t('detail.column.received')} ${line.sku}`}
            className="text-right tabular-nums"
            onChange={(e) => onChange?.({ ...line, received: e.target.value })}
          />
        ) : (
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            value={line.received}
            aria-label={`${t('detail.column.received')} ${line.sku}`}
            className="text-right tabular-nums"
            onChange={(e) => onChange?.({ ...line, received: e.target.value })}
          />
        )}
      </Field>
      <Field label={t('detail.column.diffLong')}>
        <span className={deviationClass(diff)}>{formatDeviation(diff)}</span>
      </Field>

      {showPrices && (
        <Field label={t('detail.column.pricePerPiece')}>
          {priceEditable ? (
            <div className="flex flex-col gap-1">
              <PriceInput
                value={line.price ?? ''}
                label={`${t('detail.column.pricePerPiece')} ${line.sku}`}
                onChange={(price) => onChange?.({ ...line, price })}
              />
              {lastPrice && (
                <div
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs @4xl/lines:justify-end"
                  title={t('price.lastFrom', {
                    date: formatDate(lastPrice.bookedAt),
                  })}
                >
                  <span className="text-muted-foreground">
                    {t('price.last')}{' '}
                    <span className="tabular-nums text-foreground">
                      {formatCurrency(lastPrice.unitCostCents)}
                    </span>
                  </span>
                  <CopyLastPriceButton
                    onCopy={() =>
                      onChange?.({
                        ...line,
                        price: formatPriceInput(lastPrice.unitCostCents),
                      })
                    }
                  />
                </div>
              )}
            </div>
          ) : perPiece == null ? (
            '–'
          ) : (
            formatCurrency(Math.round(perPiece))
          )}
        </Field>
      )}
      {showPrices &&
        (perPackage != null ? (
          // Phone only: the table keeps to the approved columns; the card adds the package price.
          <Field
            label={t('detail.column.pricePerPackage')}
            className="@4xl/lines:hidden"
          >
            {formatCurrency(perPackage)}
          </Field>
        ) : (
          // Holds the card's middle cell so Wert stays in the right-hand column.
          <div aria-hidden="true" className="@4xl/lines:hidden" />
        ))}
      {showPrices && (
        <Field label={t('detail.column.lineValue')}>
          {value == null ? '–' : formatCurrency(value)}
        </Field>
      )}

      {showNote && (
        <div className="col-span-3 @4xl/lines:order-last @4xl/lines:col-[1/-1]">
          {readOnly ? (
            <p className="text-sm">
              <span className="text-muted-foreground">
                {`${t('detail.column.note')}: `}
              </span>
              {line.note}
            </p>
          ) : (
            <Input
              value={line.note}
              aria-label={`${t('detail.column.note')} ${line.sku}`}
              placeholder={t('detail.column.note')}
              onChange={(e) => onChange?.({ ...line, note: e.target.value })}
            />
          )}
        </div>
      )}
    </div>
  )
}

/** The column captions, shown only where the rows actually read as a table. */
export function DeliveryCheckHeader({
  readOnly,
  showPrices = false,
}: {
  readOnly: boolean
  showPrices?: boolean
}) {
  const { t } = useTranslation('inventory-deliveries')
  const right = 'text-right'
  return (
    <div className={cn(HEADER_BASE, checkColumns(showPrices, !readOnly))}>
      <div>{t('detail.column.product')}</div>
      <div>{t('detail.column.package')}</div>
      <div className={right}>{t('detail.column.unitCount')}</div>
      <div className={right}>{t('detail.column.content')}</div>
      <div className={right}>{t('detail.column.expected')}</div>
      <div className={right}>{t('detail.column.received')}</div>
      <div className={right}>{t('detail.column.diff')}</div>
      {showPrices && (
        <div className={right}>{t('detail.column.pricePerPiece')}</div>
      )}
      {showPrices && (
        <div className={right}>{t('detail.column.lineValue')}</div>
      )}
      {!readOnly && <div />}
    </div>
  )
}

/** The footer of a position list: "Warenwert netto" and its figure, aligned under the values. */
export function GoodsValueFooter({
  label,
  cents,
}: {
  label: string
  cents: number
}) {
  return (
    <div className="flex items-center justify-between border-t bg-muted/30 px-3 py-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{formatCurrency(cents)}</span>
    </div>
  )
}

// --- Announce / edit form ---

export function DeliveryFormLineRow({
  line,
  restaurantId,
  onChange,
  onRemove,
  onQuantityEnter,
  stock,
  showPrices = false,
  lastPrice,
}: {
  line: FormLine
  restaurantId?: number
  onChange: (next: FormLine) => void
  onRemove: () => void
  /** Enter in the quantity field: the fast path to the next position (never a form submit). */
  onQuantityEnter?: () => void
  /** The picked article's counting mode, unit and stock kind, once known. */
  stock?: StockArticleInfo
  /** A hand-announced delivery: the EK netto / Stk input and the Letzter EK column exist. */
  showPrices?: boolean
  lastPrice?: DeliveryLastPrice
}) {
  const { t } = useTranslation('inventory-deliveries')
  const qtyRef = useRef<HTMLInputElement>(null)
  const fractional = stock?.fractional === true
  const unit = shownUnit(fractional, stock?.unit)
  const onQtyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onQuantityEnter?.()
    }
  }
  const copyLast = () =>
    lastPrice &&
    onChange({ ...line, price: formatPriceInput(lastPrice.unitCostCents) })

  return (
    <div
      className={cn(
        ROW_BASE,
        showPrices ? FORM_COLUMNS.priced : FORM_COLUMNS.plain,
      )}
    >
      <div className="col-span-2 min-w-0 @4xl/lines:col-span-1">
        <ProductSkuCombobox
          value={line.product}
          // A newly picked product brings its own facts; the saved line's no longer apply.
          onChange={(product) =>
            onChange({ ...line, product, stock: undefined })
          }
          // Keyboard flow: picking a product (Enter in the search) lands directly on the quantity.
          onSelect={() => setTimeout(() => qtyRef.current?.select(), 0)}
          autoFocus={line.autoFocus}
          onAutoFocusComplete={() => onChange({ ...line, autoFocus: false })}
          placeholder={t('form.searchPlaceholder')}
          restaurantId={restaurantId}
        />
        {stock?.noStock && (
          <div className="mt-1">
            <NoStockBadge reason={stock.noStock} />
          </div>
        )}
      </div>
      <div className="col-start-3 row-start-1 flex justify-end @4xl/lines:col-start-auto @4xl/lines:row-start-auto @4xl/lines:order-last">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('form.removeLine')}
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <Field label={t('form.lineExpectedQty')}>
        <div className="flex items-center gap-1">
          {fractional ? (
            // "Teilmengen zählen": a text field keeps the de-AT comma a number field drops.
            <Input
              ref={qtyRef}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={line.expectedQty}
              aria-label={t('form.lineExpectedQty')}
              placeholder={t('form.lineExpectedQty')}
              className="text-right tabular-nums"
              onChange={(e) =>
                onChange({ ...line, expectedQty: e.target.value })
              }
              onKeyDown={onQtyKeyDown}
            />
          ) : (
            <Input
              ref={qtyRef}
              type="number"
              inputMode="numeric"
              min={0}
              value={line.expectedQty}
              aria-label={t('form.lineExpectedQty')}
              placeholder={t('form.lineExpectedQty')}
              className="text-right tabular-nums"
              onChange={(e) =>
                onChange({ ...line, expectedQty: e.target.value })
              }
              onKeyDown={onQtyKeyDown}
            />
          )}
          {unit && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {unit}
            </span>
          )}
        </div>
      </Field>

      {showPrices && (
        <Field label={t('price.netPerPiece')}>
          <PriceInput
            value={line.price}
            label={t('price.netPerPiece')}
            onChange={(price) => onChange({ ...line, price })}
          />
        </Field>
      )}
      {showPrices && (
        <Field label={t('price.last')}>
          {lastPrice ? (
            <div className="flex items-center gap-1 @4xl/lines:justify-end">
              <div className="flex flex-col">
                <span>{formatCurrency(lastPrice.unitCostCents)}</span>
                <span className="text-xs text-muted-foreground">
                  <span className={'@4xl/lines:hidden'}>
                    {formatShortDate(lastPrice.bookedAt)}
                  </span>
                  <span className={'hidden @4xl/lines:inline'}>
                    {formatDate(lastPrice.bookedAt)}
                  </span>
                </span>
              </div>
              <CopyLastPriceButton onCopy={copyLast} />
            </div>
          ) : (
            '–'
          )}
        </Field>
      )}

      <div className="col-span-3 @4xl/lines:col-span-1">
        <Input
          value={line.note}
          aria-label={t('form.lineNote')}
          placeholder={t('form.lineNote')}
          onChange={(e) => onChange({ ...line, note: e.target.value })}
        />
      </div>
    </div>
  )
}

export function DeliveryFormHeader({
  showPrices = false,
}: {
  showPrices?: boolean
}) {
  const { t } = useTranslation('inventory-deliveries')
  return (
    <div
      className={cn(
        HEADER_BASE,
        showPrices ? FORM_COLUMNS.priced : FORM_COLUMNS.plain,
      )}
    >
      <div>{t('form.lineProduct')}</div>
      <div className="text-right">{t('form.lineExpectedQty')}</div>
      {showPrices && <div className="text-right">{t('price.netPerPiece')}</div>}
      {showPrices && <div className="text-right">{t('price.last')}</div>}
      <div>{t('form.lineNote')}</div>
      <div />
    </div>
  )
}
